// src/utils/arrivalWindows.js — aw-web-1a2b3c4d
//
// SINGLE SOURCE OF TRUTH for job "arrival windows" (web/ESM copy).
// House convention mirrors src/utils/permissions.js: named exports, pure
// functions, companySettings passed in.
//
// ⚠ companySettings is READ as of Seam 4 (aw-seam4). When it carries a valid
//   per-org `arrivalWindows` array, the matching window's label + times override
//   the frozen defaults below. Absent / not-an-array / malformed entry -> frozen
//   default for that key, BYTE-IDENTICAL to pre-Seam-4. All tolerance lives in
//   _resolveWindow — the one chokepoint; nothing else validates.
//
// ⚠ SERVER MIRROR: functions/utils/arrivalWindows.js is a hand-synced CommonJS
//   copy (src/ cannot be required from Cloud Functions). Label STRINGS + hour/min
//   VALUES + the _isValidWindow / _deriveShortLabel / _resolveWindow LOGIC MUST
//   stay identical to that file — both render to customers. Labels use U+2013 EN
//   DASH with surrounding spaces, matching functions/index.js:5941-5943. Change
//   one file, change the other, run the gate.
//
// ⚠ startM/endM: 0 for every frozen default, but a stored org window may set a
//   real value (7:30). Read them at every time-building call site.
//
// ⚠ ASYMMETRIC NULL HANDLING — deliberate; reproduces each call site's behaviour:
//     getArrivalWindowLabel(null) / getArrivalWindowShortLabel(null) -> ''
//     getArrivalWindow(null)             -> the MORNING window object
//     getArrivalWindowStartMinutes(null) -> 480  (morning 08:00) SILENTLY
//     getArrivalWindowEndMinutes(null)   -> 720  (morning 12:00) SILENTLY
//   Label helpers signal "no window" with ''; the object/minutes helpers fall back
//   to morning with NO signal. A future null into the minutes helpers yields
//   480/720 and no error. Add a strict variant in your seam if you need one.
//
// ⚠ SNAPSHOT (aw-seam4): buildArrivalWindowSnapshot() captures the resolved window
//   (key, label, shortLabel, times) at job-create time; getJobArrivalWindow*Label()
//   render snapshot-first, else live-resolve the bare key. Pre-Seam-4 jobs have no
//   snapshot and resolve live -> frozen defaults, matching prior render.
//
// KEYS: 'morning' | 'afternoon' | 'allday'. 'allday' canonical; 'anytime' aliases
// to 'allday' on READ — a logged bug fix (see commit), NOT a migration.

const ARRIVAL_WINDOWS = [
  Object.freeze({ key: 'morning',   label: 'Morning (8am – 12pm)',   shortLabel: 'Morning',   startH: 8,  startM: 0, endH: 12, endM: 0 }),
  Object.freeze({ key: 'afternoon', label: 'Afternoon (12pm – 5pm)', shortLabel: 'Afternoon', startH: 12, startM: 0, endH: 17, endM: 0 }),
  Object.freeze({ key: 'allday',    label: 'All Day (8am – 5pm)',    shortLabel: 'All Day',   startH: 8,  startM: 0, endH: 17, endM: 0 }),
];
Object.freeze(ARRIVAL_WINDOWS);

const KEY_ALIASES = Object.freeze({ anytime: 'allday' });
const DEFAULT_KEY = 'morning';
const _byKey = Object.freeze(Object.fromEntries(ARRIVAL_WINDOWS.map(w => [w.key, w])));

// Normalize any stored/legacy key to a canonical key. Nullish/unknown -> fallback.
export const resolveArrivalWindowKey = (key, fallbackKey = DEFAULT_KEY) => {
  if (key == null) return fallbackKey;
  const aliased = KEY_ALIASES[key] || key;
  return _byKey[aliased] ? aliased : fallbackKey;
};

// aw-seam4 — a stored window entry is VALID only if every hour/min is an integer
// in range AND start != end. Inverted (end < start) is a valid OVERNIGHT window
// (handled by the +1440 wrap); only the exactly-equal / zero-length case is
// rejected as an ambiguous 24h window (D12).
const _isValidWindow = (w) =>
  w != null &&
  Number.isInteger(w.startH) && w.startH >= 0 && w.startH <= 23 &&
  Number.isInteger(w.startM) && w.startM >= 0 && w.startM <= 59 &&
  Number.isInteger(w.endH)   && w.endH   >= 0 && w.endH   <= 23 &&
  Number.isInteger(w.endM)   && w.endM   >= 0 && w.endM   <= 59 &&
  !(w.startH === w.endH && w.startM === w.endM);

// aw-seam4 — short label derived from the (possibly custom) full label so an org
// names each window ONCE. Strip a trailing "(...)" portion; empty result -> the
// frozen default short label. NOTE: this strips ANY trailing parenthetical, so a
// name like "Morning (crew A)" -> "Morning" — accepted; the time-suffix strip is
// the common case and parens-in-a-window-name are unusual.
const _deriveShortLabel = (label, fallbackShort) => {
  const stripped = (typeof label === 'string' ? label : '').replace(/\s*\([^)]*\)\s*$/, '').trim();
  return stripped || fallbackShort;
};

// aw-seam4 — THE single tolerance point. Returns a guaranteed-valid window object
// for `key`: a valid stored org window if present, else the frozen default. Never
// throws, never returns undefined, never yields NaN downstream. When no settings
// are stored it returns the SAME frozen object pre-Seam-4 code returned.
const _resolveWindow = (key, companySettings) => {
  const canonicalKey = resolveArrivalWindowKey(key);     // 'morning' | 'afternoon' | 'allday'
  const fallback = _byKey[canonicalKey];                 // frozen default — always valid
  const stored = companySettings && companySettings.arrivalWindows;
  if (!Array.isArray(stored)) return fallback;
  const entry = stored.find(w => w && w.key === canonicalKey);
  if (!_isValidWindow(entry)) return fallback;           // absent / malformed -> frozen default
  const label = (typeof entry.label === 'string' && entry.label) ? entry.label : fallback.label;
  return {
    key: canonicalKey,
    label,
    shortLabel: _deriveShortLabel(label, fallback.shortLabel),
    startH: entry.startH, startM: entry.startM,
    endH: entry.endH, endM: entry.endM,
  };
};

// Ordered list for dropdowns. Returns fresh COPIES; the frozen sources stay safe.
// aw-seam5 — now honors per-window `enabled`. Absent entry / no settings => shown
// (default-on, matches the editor's default). FLOOR: if a stored state disables ALL
// three, fall back to showing all — a job form must never present an empty window list.
// _resolveWindow / _isValidWindow are untouched.
export const getArrivalWindows = (companySettings) => {
  const stored = companySettings && companySettings.arrivalWindows;
  const isEnabled = (key) => {
    if (!Array.isArray(stored)) return true;
    const entry = stored.find(w => w && w.key === key);
    return entry ? entry.enabled !== false : true;
  };
  const list = ARRIVAL_WINDOWS.filter(w => isEnabled(w.key))
    .map(w => ({ ..._resolveWindow(w.key, companySettings) }));
  return list.length ? list : ARRIVAL_WINDOWS.map(w => ({ ..._resolveWindow(w.key, companySettings) }));
};

// Window object; ALWAYS returns a valid window — morning for null/unknown.
export const getArrivalWindow = (key, companySettings) => _resolveWindow(key, companySettings);

// Canonical FULL label (customer-facing). null -> ''. Unknown key -> the raw key.
export const getArrivalWindowLabel = (key, companySettings) => {
  if (key == null) return '';
  const aliased = KEY_ALIASES[key] || key;
  if (!_byKey[aliased]) return String(key);
  return _resolveWindow(key, companySettings).label;
};

// SHORT label ('Morning'). null -> ''. Unknown key -> the raw key.
export const getArrivalWindowShortLabel = (key, companySettings) => {
  if (key == null) return '';
  const aliased = KEY_ALIASES[key] || key;
  if (!_byKey[aliased]) return String(key);
  return _resolveWindow(key, companySettings).shortLabel;
};

// Start minutes-from-midnight. null -> 480 (morning), SILENTLY.
export const getArrivalWindowStartMinutes = (key, companySettings) => {
  const w = _resolveWindow(key, companySettings);
  return w.startH * 60 + w.startM;
};

// Wrap-adjusted END minutes for capacity math. Same-day today; an overnight
// window (end <= start) rolls into the next day. null -> 720 (morning), SILENTLY.
export const getArrivalWindowEndMinutes = (key, companySettings) => {
  const w = _resolveWindow(key, companySettings);
  const startTotal = w.startH * 60 + w.startM;
  const endTotal   = w.endH * 60 + w.endM;
  return endTotal <= startTotal ? endTotal + 1440 : endTotal;
};

// aw-seam4 — SNAPSHOT builders/readers.
// Build the immutable snapshot to WRITE at job-create time (window mode only).
// Carries the derived shortLabel so chip render sites need no companySettings.
export const buildArrivalWindowSnapshot = (key, companySettings) => {
  if (key == null) return null;
  const w = _resolveWindow(key, companySettings);
  return {
    key: w.key, label: w.label, shortLabel: w.shortLabel,
    startH: w.startH, startM: w.startM, endH: w.endH, endM: w.endM,
  };
};

// RENDER a job's FULL label: snapshot-first (historical), else live-resolve its key.
export const getJobArrivalWindowLabel = (job, companySettings) =>
  (job && job.arrivalWindowSnapshot && job.arrivalWindowSnapshot.label)
    ? job.arrivalWindowSnapshot.label
    : getArrivalWindowLabel(job ? job.arrivalWindow : null, companySettings);

// RENDER a job's SHORT label (chip): snapshot-first, else live-resolve its key.
export const getJobArrivalWindowShortLabel = (job, companySettings) =>
  (job && job.arrivalWindowSnapshot && job.arrivalWindowSnapshot.shortLabel)
    ? job.arrivalWindowSnapshot.shortLabel
    : getArrivalWindowShortLabel(job ? job.arrivalWindow : null, companySettings);
